import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Mail, CalendarHeart, FileText, Shield } from "lucide-react";
import { PopupButton } from "react-calendly";
import { trackContact } from "../lib/analytics";
import { trackContactWithData } from "../lib/appTracking";
import useAuth from "../hooks/useAuth";
import ContactForm from "./ContactForm";

function ContactUs() {
  const { t } = useTranslation();
  const { auth } = useAuth();

  const handleEmailClick = () => {
    trackContact();
    trackContactWithData({ userEmail: auth?.email, method: "email" });
  };

  const handleCalendlyClick = () => {
    trackContact();
    trackContactWithData({ userEmail: auth?.email, method: "calendly" });
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">

      {/* Hero */}
      <div className="mb-10 text-center">
        {/* Illustration */}
        <div className="flex justify-center mb-5" aria-hidden="true">
          <svg width="96" height="72" viewBox="0 0 96 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Left chat bubble */}
            <rect x="2" y="8" width="40" height="28" rx="10" fill="hsl(var(--primary))" opacity="0.15"/>
            <rect x="2" y="8" width="40" height="28" rx="10" stroke="hsl(var(--primary))" strokeWidth="1.5" opacity="0.5"/>
            <circle cx="14" cy="22" r="3" fill="hsl(var(--primary))" opacity="0.6"/>
            <circle cx="22" cy="22" r="3" fill="hsl(var(--primary))" opacity="0.6"/>
            <circle cx="30" cy="22" r="3" fill="hsl(var(--primary))" opacity="0.6"/>
            {/* Tail left bubble */}
            <path d="M12 36 L8 44 L20 38" fill="hsl(var(--primary))" opacity="0.15"/>
            <path d="M12 36 L8 44 L20 38" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" opacity="0.5"/>
            {/* Right chat bubble */}
            <rect x="54" y="20" width="40" height="28" rx="10" fill="hsl(var(--primary))" opacity="0.25"/>
            <rect x="54" y="20" width="40" height="28" rx="10" stroke="hsl(var(--primary))" strokeWidth="1.5" opacity="0.7"/>
            <line x1="64" y1="30" x2="84" y2="30" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
            <line x1="64" y1="37" x2="76" y2="37" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
            {/* Tail right bubble */}
            <path d="M84 48 L88 56 L76 50" fill="hsl(var(--primary))" opacity="0.25"/>
            <path d="M84 48 L88 56 L76 50" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" opacity="0.7"/>
            {/* Small heart between bubbles */}
            <path d="M46 26 C46 24.3 47.1 23 48 23 C48.9 23 50 24.3 50 26 C50 28 48 30 48 30 C48 30 46 28 46 26Z" fill="hsl(var(--primary))" opacity="0.8"/>
          </svg>
        </div>
        <h1 className="text-2xl font-bold mb-2">
          {t("contact_hero_title") || "Let's talk"}
        </h1>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
          {t("contact_hero_description") ||
            "We're a small, hands-on team. Whether you have a question, an idea, or just want to say hi — we'd love to hear from you."}
        </p>
      </div>

      {/* Contact methods */}
      <div className="grid gap-3 sm:grid-cols-2 mb-10">
        <a
          href="mailto:doron@clarynext.com"
          onClick={handleEmailClick}
          className="flex items-start gap-3 p-4 bg-background border rounded-xl hover:border-primary hover:shadow-sm transition-all group no-underline"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/15 transition-colors">
            <Mail className="h-4 w-4 text-primary" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">
              {t("send_an_email") || "Send an email"}
            </p>
            <p className="text-xs text-primary mt-0.5">doron@clarynext.com</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("email_response_time") || "We usually reply within 1–2 business days"}
            </p>
          </div>
        </a>

        <div className="flex items-start gap-3 p-4 bg-background border rounded-xl hover:border-primary hover:shadow-sm transition-all group cursor-pointer calendly-container">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/15 transition-colors">
            <CalendarHeart className="h-4 w-4 text-primary" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">
              {t("book_a_call") || "Book a call"}
            </p>
            <div className="text-xs text-primary mt-0.5">
              <PopupButton
                url="https://calendly.com/clarynext/doron"
                rootElement={document.getElementById("root")}
                text={t("schedule_with_doron") || "Schedule time with Doron →"}
                textColor="currentColor"
                color="transparent"
                className="!p-0 !bg-transparent !border-0 !shadow-none text-primary hover:underline text-xs"
                onClick={handleCalendlyClick}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("call_description") || "30 min — pick a time that works for you"}
            </p>
          </div>
        </div>
      </div>

      {/* Contact form */}
      <div className="mb-10">
        <h2 className="text-base font-semibold mb-3">
          {t("send_a_message") || "Send a message"}
        </h2>
        <ContactForm />
      </div>

      {/* Founder card */}
      <div className="mb-10">
        <h2 className="text-base font-semibold mb-3">
          {t("meet_the_founder") || "Who you're talking to"}
        </h2>
        <div className="flex items-start gap-4 p-4 bg-background border rounded-xl">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-lg select-none">
            D
          </div>
          <div>
            <p className="font-semibold text-sm text-foreground">
              {t("founder_name") || "Doron"}
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              {t("founder_role") || "Founder & CEO"}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("founder_bio") || "I built ClaryNext because I believe structured thinking leads to real results. I read every message personally and love hearing from people who care about better learning and coaching."}
            </p>
          </div>
        </div>
      </div>

      {/* Legal — lightweight footer row */}
      <div className="border-t pt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
        <Link
          to="/terms"
          className="flex items-center gap-1 hover:text-foreground transition-colors no-underline"
        >
          <FileText className="h-3 w-3" />
          {t("terms_of_service")}
        </Link>
        <Link
          to="/privacy"
          className="flex items-center gap-1 hover:text-foreground transition-colors no-underline"
        >
          <Shield className="h-3 w-3" />
          {t("privacy_policy")}
        </Link>
      </div>
    </div>
  );
}

export default ContactUs;
